import { Mic, Flame } from "lucide-react";
import { useEffect, useRef } from "react";
import { GameBadge } from "./GameBadge";
import { minScoringWords, scoringHint, zeroPointsMessage } from "@/lib/scoringWords";
import { starterTexts } from "@/lib/starter";

// Round S — live nudge on the review step; never blocks saving, never shown to a lezer.
export const ScoringHint = ({ text, question, week, user }) => {
  if (!user || user.role === "lezer") return null;
  const hint = scoringHint({ text, questionId: question?.id, questionCategory: question?.category,
    focusCategory: week?.focus_category, min: minScoringWords(week) });
  const tone = hint.state === "scores" ? "text-tw-muted" : "text-tw-yellow/90";
  return <p className={`font-mono text-[11px] leading-relaxed break-words ${tone}`} data-testid="scoring-hint" data-state={hint.state}>
    {hint.text}
  </p>;
};

export const StreakChip = ({ streak, prefix = "game-streak" }) => {
  if (!streak || streak.available === false || !streak.total_memos) return null;
  const milestone = [12, 8, 4, 2].find(n => streak.current_weeks >= n);
  return <div className="game-streak" data-testid={prefix}>
    {milestone ? <GameBadge milestone={milestone} label={`${milestone} weken op rij`} testId={`${prefix}-badge`} /> : <Flame size={16} aria-hidden />}
    <span data-testid={`${prefix}-current`}>{streak.current_weeks} {streak.current_weeks === 1 ? "week" : "weken"} op rij</span>
    <span className="text-tw-muted" data-testid={`${prefix}-done`}>{streak.this_week_done ? "Deze week ✓" : "Deze week nog open"}</span>
    <span className="sr-only" data-testid={`${prefix}-best`}>Langste reeks: {streak.best_weeks} weken</span>
  </div>;
};

export const StarterCard = ({ question, mode, user, onPick, onLater }) => {
  if (!question || !mode) return null;
  const { title, body } = starterTexts({ mode, user, question });
  return <section className="game-starter" data-testid="game-starter" data-mode={mode}>
    <h2 className="font-display text-base md:text-lg font-bold" data-testid="game-starter-title">{title}</h2>
    <p className="text-sm text-tw-muted mt-1" data-testid="game-starter-body">{body}</p>
    <p className="text-white my-4 break-words" data-testid="game-starter-question">{question.text}</p>
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" onClick={() => onPick(question)} className="game-primary" data-testid="game-starter-start"><Mic size={16} />Beantwoord deze vraag</button>
      <button type="button" onClick={onLater} data-testid="game-starter-later"
        className="tw-press h-10 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-tw-muted hover:text-white transition-colors">Later</button>
    </div>
  </section>;
};

export const SaveReward = ({ reward }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (reward?.streak) ref.current?.scrollIntoView({
      block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  }, [reward]);
  if (!reward?.streak) return null;
  const me = reward.week?.standings?.find(r => r.is_me);
  const ahead = reward.week?.standings?.filter(r => me && r.points > me.points).slice(-1)[0];
  const zero = zeroPointsMessage(reward);
  return <div ref={ref} className="game-reward tw-fadeup" role="status" data-testid="game-save-reward">
    {reward.points > 0 && <strong className="text-tw-yellow" data-testid="game-reward-points">+{reward.points} {reward.points === 1 ? "punt" : "punten"}</strong>}
    {zero && <p className="text-tw-yellow/90 break-words" data-testid="game-reward-zero" data-reason={reward.reason}>{zero}</p>}
    {me && reward.streak.total_memos >= 1 && reward.points > 0 && <p data-testid="game-reward-position">Je staat nu {me.position}e{ahead ? `, ${ahead.points - me.points} ${ahead.points - me.points === 1 ? "punt" : "punten"} achter ${ahead.name}` : ", bovenaan deze week"}.</p>}
    <p data-testid="game-reward-streak">{reward.streak.current_weeks} {reward.streak.current_weeks === 1 ? "week" : "weken"} op rij. Mooi vastgelegd.</p>
  </div>;
};